import {
    Build, BuildCheckSum,
    BuildReleaseNotesModificationRequest, BuildVersionCompatability, PromoteBuildByVersionRequest
} from "../../models/build";
import {LicenseKey, LicenseKeyRequest, LicensePrice, Payment} from "../../models/license";
import {BlogPost, Category, Tag, Testimonial} from "../../models/site";
import {User} from "../../models/user";
import {NewsLetter, NewsletterSubscriber} from "../../models/newsletter";
import {FeatureRequest, SupportTicket} from "../../models/support";
import {
    CheckoutSession,
    RefundPayment,
    VerifyPaymentBody
} from "../../models/checkout";
import {getAccessToken} from "../credential_manager";

export async function WebClient(endpoint: string, method = "GET", body: any | undefined = undefined) {
    const baseUrl = "https://api.pennypusher.net"

    const accessToken = getAccessToken();

    if (!accessToken) {
        return {
            status: 403,
            data: { error: "Access token is missing, empty, or expired" },
        };
    }

    let headers: HeadersInit = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${accessToken}`,
    };

    let config: RequestInit = {
        method: method,
        headers: headers,
    };

    if (body !== undefined) {
        config.body = JSON.stringify(body);
    }

    try {
        const response = await fetch(encodeURI(baseUrl + endpoint), config);

        // Attempt to parse JSON response
        let data;
        try {
            data = await response.json();
        } catch {
            data = { error: "Invalid JSON response" };
        }

        if (!response.ok) {
            return {
                status: response.status,
                data: data || { error: response.statusText },
                url: response.url,
            };
        }

        return {
            status: response.status,
            data: data,
            url: response.url,
        };
    } catch (e: any) {
        return {
            status: 500,
            data: { error: e.message || "Network error" },
        };
    }
}

// ---------------------------------------- SYSTEM STATS ----------------------------------------
WebClient.GetAdminStats = function (){
    return WebClient(`/api/v1/admin/statistics/`, "GET");
}

// ---------------------------------------- USER ----------------------------------------
WebClient.GetAllUsers = function (page:number){
    return WebClient(`/api/v1/admin/users/all/?page:${page}`, "GET");
}

WebClient.DeleteUser = function (email: string){
    return WebClient(`/api/v1/admin/user/delete?username=${email}`, "DELETE");
}

WebClient.CreateNewUser = function (user: User){
    return WebClient(`/api/v1/user/register`, "POST", user);
}

WebClient.GetUserDetails = function (){
    return WebClient("/api/v1/user/details", "GET");
}

WebClient.InviteUserToDevGroup = function (user: User){
    return WebClient("/api/v1/admin/user/invite/developer/", "PATCH", user);
}

WebClient.InviteUserToBetaTesterGroup = function (user: User){
    return WebClient("/api/v1/admin/user/invite/beta_tester/", "PATCH", user);
}

WebClient.ModifyUser = function (username: string, email: string, first_name: string, last_name: string){
    const body = {
        "username": username,
        "email": email,
        "first_name": first_name,
        "last_name": last_name
    }
    return WebClient("/api/v1/user/modify", "PATCH", body);
}

WebClient.ChangePassword = function (password:string){
    const body = {
        "password": password
    }
    return WebClient("/api/v1/user/modify", "PATCH", body);
}

// ---------------------------------------- BUILD ----------------------------------------

WebClient.AddBuild = function (build: Build){
    return WebClient("/api/v1/admin/build/new", "POST", build);
}

WebClient.GetBuildList = function (page:number){
    return WebClient(`/api/v1/build/list?page=${page}`, "GET");
}

WebClient.AdminModifyBuildReleaseNotes = function (body: BuildReleaseNotesModificationRequest){
    return WebClient(`/api/v1/admin/build/update/release_notes`, "PATCH", body);
}

WebClient.DeleteBuild = function (id:string){
    return WebClient(`/api/v1/admin/build/delete?id=${id}`, "DELETE");
}

export interface AdminUpdateBuildPayload {
    id: string;
    link?: string;
    release_link?: string;
    checksum?: string;
}

WebClient.AdminUpdateBuild = function (payload: AdminUpdateBuildPayload){
    return WebClient("/api/v1/admin/build/update", "PATCH", payload);
}

WebClient.GetAllBuildDownloadHistory = function (page:number){
    return WebClient(`/api/v1/build/admin/download_history/all?page=${page}`, "GET");
}

WebClient.AddNewCheckSum = function (sum: BuildCheckSum){
    return WebClient("/api/v1/build/checksum/new", "POST", sum);
}

WebClient.ValidateCheckSum = function (sum: BuildCheckSum){
    return WebClient("/api/v1/build/checksum/new", "POST", sum);
}

WebClient.GetAllChecksumPaginated = function (page:number){
    return WebClient(`/api/v1/build/checksum/list?page=${page}`, "GET");
}

WebClient.AdminPromoteBuild = function (build: Build){
    return WebClient("/api/v1/admin/build/promote/", "POST", build);
}

WebClient.AdminPromotedBuildsByVersion = function (request: PromoteBuildByVersionRequest){
    return WebClient("/api/v1/admin/build/promote_version/", "POST", request);
}


// ---------------------------------------- BUILD VERSION COMPATIBILITY ----------------------------------------
WebClient.AdminNewBuildVersionCompatibility = function (comp: BuildVersionCompatability){
    return WebClient("/api/v1/admin/build/version_compatibility/", "POST", comp);
}

WebClient.AdminGetBuildVersionCompatibility = function (server_version: string | undefined, client_version: string | undefined){
    let url = "/api/v1/admin/build/version_compatibility/";
    
    const params = new URLSearchParams();
    if (server_version && server_version.length > 0) {
        params.append("server_version", server_version);
    }
    if (client_version && client_version.length > 0) {
        params.append("client_version", client_version);
    }
    
    const queryString = params.toString();
    if (queryString) {
        url += `?${queryString}`;
    }
    
    return WebClient(url, "GET");
}

WebClient.AdminModifyBuildVersionCompatibility = function (comp: BuildVersionCompatability){
    return WebClient("/api/v1/admin/build/version_compatibility/", "PATCH", comp);
}

WebClient.AdminDeleteBuildVersionCompatibility = function (server_version: string, client_version: string){
    return WebClient(`/api/v1/admin/build/version_compatibility/?server_version=${server_version}
    &client_version=${client_version}`, "DELETE");
}

WebClient.UserGetBuildVersionCompatibility = function (){
    return WebClient("/api/v1/user/build/version_compatibility/", "GET");
}

WebClient.PublicGetBuildVersionCompatibility = function (){
    return WebClient("/api/v1/build/version_compatibility/", "GET");
}

// ---------------------------------------- LICENSE ----------------------------------------
WebClient.AdminNewLicenseKey = function (license:LicenseKeyRequest){
    return WebClient(`/api/v1/admin/license/`, "POST", license);
}

WebClient.AdminGetLicenseKeysPaginated = function (page:number){
    return WebClient(`/api/v1/admin/license/?page=${page}`, "GET");
}

WebClient.AdminUpdateLicenseKey = function (license:LicenseKeyRequest){
    return WebClient(`/api/v1/admin/license/`, "PATCH", license);
}

WebClient.AdminDeleteLicenseKey =function (license:LicenseKey){
    return WebClient(`/api/v1/admin/license/?key=${license.key}`, "DELETE");
}

WebClient.UserNewFreeTrialLicense = function (){
    return WebClient(`/api/v1/license/`, "POST");
}

WebClient.UserGetLicenseKeysPaginated = function (page:number){
    return WebClient(`/api/v1/license/?page=${page}`, "GET");
}


// ---------------------------------------- PAYMENT ----------------------------------------
WebClient.AdminGetPaymentsPaginated = function (page:number){
    return WebClient(`/api/v1/admin/payment/?page=${page}`, "GET");
}

WebClient.AdminRefundPayment = function (refund:RefundPayment){
    return WebClient(`/api/v1/admin/payment/refund/`, "POST", refund);
}

WebClient.CreateCheckoutSession = function (session:CheckoutSession){
    return WebClient(`/api/v1/payment/checkout/create/`, "POST", session);
}

WebClient.VerifyPayment = function (verifyBody:VerifyPaymentBody){
    return WebClient(`/api/v1/payment/verify/`, "POST", verifyBody);
}

WebClient.GetStripeWebhook = function (payment:Payment){
    return WebClient(`/api/v1/payment/webhook/`, "POST", payment);
}


// ---------------------------------------- License Price ----------------------------------------
// todo remove
WebClient.NewLicensePrice = function (lPrice: LicensePrice){
    return WebClient("/api/v1/license/price/new", "POST", lPrice);
}

WebClient.GetAllLicensePrices = function (){
    return WebClient("/api/v1/license_price/all", "GET");
}

WebClient.GetAllActiveLicensePrices = function (){
    return WebClient("/api/v1/license_price/all", "GET");
}

// ---------------------------------------- SITE ----------------------------------------
WebClient.GetAllCategories = function (){
    return WebClient("/api/v1/site/category/all", "GET");
}

WebClient.NewCategory = function (category: Category){
    return WebClient("/api/v1/site/category/new", "POST", category);
}

WebClient.GetAllTags = function (){
    return WebClient("/api/v1/site/tag/all", "GET");
}

WebClient.NewTag = function (tag: Tag){
    return WebClient("/api/v1/site/tag/new", "POST", tag);
}

WebClient.NewBlogPost = function (blog: BlogPost){
    return WebClient("/api/v1/site/blog/new", "POST", blog);
}

WebClient.GetAllBlogs = function (){
    return WebClient("/api/v1/site/blog/all", "GET");
}

WebClient.ModifyBlogPost = function (blog: BlogPost){
    return WebClient("/api/v1/site/blog/modify", "PUT", blog);
}

// ---------------------------------------- TESTIMONIAL ----------------------------------------

WebClient.AdminGetTestimonialPaginated = function (page: number){
    return WebClient(`/api/v1/admin/testimonial/?page=${page}`, "GET");
}

WebClient.AdminGetTestimonial = function (testimonial: Testimonial){
    return WebClient(`/api/v1/admin/testimonial/?id=${testimonial.id}`, "GET");
}

WebClient.AdminApproveTestimonial = function (testimonial: Testimonial){
    const body: any = {
        ...testimonial,
    };
    // Remove user field from body before sending
    delete body.user;
    return WebClient(`/api/v1/admin/testimonial/`, "PATCH", body);
}

WebClient.AdminDeleteTestimonial = function (testimonial: Testimonial){
    return WebClient(`/api/v1/admin/testimonial/?id=${testimonial.id}`, "DELETE");
}

WebClient.UserNewTestimonial = function (testimonial: Testimonial){
    return WebClient(`/api/v1/user/testimonial/`, "POST", testimonial);
}

WebClient.UserGetTestimonialPaginated = function (page: number){
    return WebClient(`/api/v1/user/testimonial/?page=${page}`, "GET");
}

WebClient.UserGetTestimonial = function (testimonial: Testimonial){
    return WebClient(`/api/v1/user/testimonial/?id=${testimonial.id}`, "GET");
}

WebClient.UserUpdateTestimonial = function (testimonial: Testimonial){
    const body: any = {
        ...testimonial,
    };
    // Remove user field from body before sending
    delete body.user;
    return WebClient(`/api/v1/user/testimonial/`, "PATCH", body);
}

WebClient.UserDeleteTestimonial = function (testimonial: Testimonial){
    return WebClient(`/api/v1/user/testimonial/?id=${testimonial.id}`, "DELETE");
}


// ---------------------------------------- SUPPORT ----------------------------------------
WebClient.AdminGetListSupportTickets = function (page: number){
    return WebClient(`/api/v1/admin/support_ticket/?page=${page}`, "GET");
}

WebClient.AdminGetSupportTicket = function (ticket: SupportTicket){
    return WebClient(`/api/v1/admin/support_ticket/?id=${ticket.id}`, "GET");
}

WebClient.AdminUpdateSupportTicket = function (ticket: SupportTicket){
    const body: any = {
        ...ticket,
    };
    // Remove user field from body before sending
    delete body.user;
    return WebClient(`/api/v1/admin/support_ticket/`, "PATCH", body);
}

WebClient.AdminDeleteSupportTicket = function (ticket: SupportTicket){
    return WebClient(`/api/v1/admin/support_ticket/?id=${ticket.id}`, "DELETE");
}

WebClient.NewSupportTicket = function (ticket: SupportTicket){
    return WebClient("/api/v1/support_ticket/", "POST", ticket);
}

WebClient.GetListSupportTickets = function (page: number){
    return WebClient(`/api/v1/support_ticket/?page=${page}`, "GET");
}

WebClient.GetSupportTicket = function (ticket: SupportTicket){
    return WebClient(`/api/v1/support_ticket/?id=${ticket.id}`, "GET");
}

WebClient.DeleteSupportTicket = function (ticket: SupportTicket){
    return WebClient(`/api/v1/support_ticket/?id=${ticket.id}`, "DELETE");
}


// ---------------------------------------- FEATURE REQUESTS ----------------------------------------
WebClient.AdminGetListFeatureRequests = function (page: number){
    return WebClient(`/api/v1/admin/feature_request/?page=${page}`, "GET");
}

WebClient.AdminGetFeatureRequest = function (request: FeatureRequest){
    return WebClient(`/api/v1/admin/feature_request/?id=${request.id}`, "GET");
}

WebClient.AdminUpdateFeatureRequest = function (request: FeatureRequest){
    const body: any = {
        ...request,
    };
    // Remove user field from body before sending
    delete body.user;
    return WebClient(`/api/v1/admin/feature_request/`, "PATCH", body);
}

WebClient.AdminDeleteFeatureRequest = function (request: FeatureRequest){
    return WebClient(`/api/v1/admin/feature_request/?id=${request.id}`, "DELETE");
}

WebClient.NewFeatureRequest = function (request: FeatureRequest){
    return WebClient(`/api/v1/feature_request/`, "POST", request);
}

WebClient.GetListFeatureRequests = function (page: number){
    return WebClient(`/api/v1/feature_request/?page=${page}`, "GET");
}

WebClient.GetFeatureRequest = function (request: FeatureRequest){
    return WebClient(`/api/v1/feature_request/?id=${request.id}`, "GET");
}

WebClient.DeleteFeatureRequest = function (request: FeatureRequest){
    return WebClient(`/api/v1/feature_request/?id=${request.id}`, "DELETE");
}

// ---------------------------------------- NEWSLETTER ----------------------------------------
WebClient.NewNewsletter = function (newsletter: NewsLetter){
    return WebClient("/api/v1/admin/newsletter/", "POST", newsletter);
}

WebClient.GetNewsletter = function (id: number){
    return WebClient(`/api/v1/admin/newsletter/?id=${id}`, "GET");
}

WebClient.GetNewsletterList = function (page: number){
    return WebClient(`/api/v1/admin/newsletter/?page=${page}`, "GET");
}

WebClient.UpdateNewsletter = function (newsletter: NewsLetter){
    return WebClient("/api/v1/admin/newsletter/", "PATCH", newsletter);
}

WebClient.DeleteNewsletter = function (id: number){
    return WebClient(`/api/v1/admin/newsletter/?id=${id}`, "DELETE");
}

// ---------------------------------------- NEWSLETTER SUBSCRIBER ----------------------------------------
/**
 * @param page
 * @constructor
 * @deprecated
 */
WebClient.GetAllNewsletterSubscribers = function (page: number){
    return WebClient(`/api/v1/admin/newsletter/subscriber/?page=${page}`, "GET");
}

WebClient.ToggleNewsletterSubscriberStatus = function (subscriber: NewsletterSubscriber){
    return WebClient(`/api/v1/admin/newsletter/subscriber/`, "PATCH", subscriber);
}

WebClient.DeleteNewsletterSubscriber = function (id: string){
    return WebClient(`/api/v1/admin/newsletter/subscriber/?id=${id}`, "DELETE");
}
